import './assets/styles/colors.css'
import './assets/styles/fonts.css'
import './assets/styles/breakpoints.css'

import App from './App.vue'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import router from './router'

// Vuetify
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import '@mdi/font/css/materialdesignicons.css'

const vuetify = createVuetify({
  components,
  directives,
  display: {
    thresholds: {
      xs: 0,
      sm: 767,    // mobile jusqu'à 767px inclus
      md: 1023,   // tablet jusqu'à 1023px inclus
      lg: 1023,   // même valeur que md car non utilisé
      xl: 1023,   // même valeur que md car non utilisé
    },
  },
  theme: {
    defaultTheme: 'ethicc',
    themes: {
      ethicc: {
        dark: false,
        colors: {
          primary: '#3e7457', // Forest Green (brand-green-40)
          secondary: '#2d5a42', // Dark Green (brand-green-30)
          background: '#FAF6F1', // Warm Cream (neutral-97)
          surface: '#ffffff',
          'surface-variant': '#f0ece6', // neutral-92
          'on-surface-variant': '#6b6560', // neutral-45
          'on-surface': '#1a1816', // neutral-10
          'on-background': '#6b6560', // neutral-45
          error: '#ef4444',
          info: '#3b82f6',
          success: '#16a34a',
          warning: '#c9a227',
        },
      },
    },
  },
  defaults: {
    global: {
      ripple: false,
    },
    VBtn: {
      class: 'text-none',
      style: 'border-radius: 6px; font-weight: 500; letter-spacing: 0.3px;',
    },
    VCard: {
      elevation: 0,
      style: 'border: 1px solid rgba(var(--v-theme-surface-variant), 0.1);',
    },
    VAppBar: {
      elevation: 0,
      style: 'border-bottom: 1px solid rgba(var(--v-theme-surface-variant), 0.1);',
    },
  },
})

// Appliquer les styles globaux
const style = document.createElement('style')
style.textContent = `
  :root {
    --font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    --v-border-color: var(--v-theme-on-surface);
  }

  html {
    font-family: var(--font-family) !important;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .v-application {
    font-family: var(--font-family) !important;
    background-color: rgb(var(--v-theme-background)) !important;
    letter-spacing: -0.2px;
  }

  .v-application [class*='text-'] {
    font-family: var(--font-family) !important;
  }

  .v-card {
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
    background: rgb(var(--v-theme-surface)) !important;
  }

  .v-card:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 0 0 1px rgba(var(--v-theme-primary), 0.2),
                0 4px 12px -2px rgba(var(--v-theme-primary), 0.12) !important;
  }

  .v-btn {
    text-transform: none !important;
    font-weight: 600 !important;
    transition: all 0.2s ease !important;
    letter-spacing: -0.2px !important;
  }

  .v-btn:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 0 0 1px rgba(var(--v-theme-primary), 0.2),
                0 4px 12px -2px rgba(var(--v-theme-primary), 0.12) !important;
  }

  .v-btn.v-btn--variant-flat {
    box-shadow: 0 0 0 1px rgba(var(--v-theme-surface-variant), 0.1) !important;
  }

  .v-app-bar {
    backdrop-filter: blur(12px) !important;
    background: rgba(var(--v-theme-background), 0.7) !important;
  }

  .v-text-field.v-text-field--solo .v-field__input {
    background: rgb(var(--v-theme-surface)) !important;
    border: 1px solid rgba(var(--v-theme-surface-variant), 0.1) !important;
  }

  /* Style pour les titres */
  .text-h4, .text-h5, .text-h6 {
    font-weight: 600 !important;
    letter-spacing: -0.5px !important;
    line-height: 1.2 !important;
  }

  .text-h4 {
    font-size: 2rem !important;
  }

  .text-h5 {
    font-size: 1.5rem !important;
  }

  .text-h6 {
    font-size: 1.25rem !important;
  }

  /* Effet de lueur bleue sur les éléments primaires */
  .v-btn--variant-elevated.v-btn--color-primary {
    box-shadow: 0 0 20px -8px rgb(var(--v-theme-primary)) !important;
  }

  /* Ajustements de la typographie */
  .v-card-title {
    font-weight: 600 !important;
    letter-spacing: -0.3px !important;
  }

  .v-card-text {
    font-weight: 400 !important;
    line-height: 1.6 !important;
    letter-spacing: -0.1px !important;
  }

  /* Styles pour la mise en page Supabase */
  .v-navigation-drawer {
    background-color: rgb(var(--v-theme-background)) !important;
  }

  .v-list {
    background-color: transparent !important;
  }

  .v-list-item {
    min-height: 40px !important;
    padding-inline: 12px !important;
  }

  .v-list-item__prepend {
    padding-inline-end: 12px !important;
  }

  .v-list-item-title {
    font-size: 0.875rem !important;
    font-weight: 400 !important;
    letter-spacing: -0.2px !important;
  }

  .v-field.v-field--variant-solo {
    box-shadow: none !important;
    background-color: rgb(var(--v-theme-surface)) !important;
    border: 1px solid rgba(var(--v-border-color), 0.08) !important;
  }

  .v-field.v-field--variant-solo .v-field__input {
    min-height: 36px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .v-divider {
    border-color: rgba(var(--v-border-color), 0.08) !important;
  }
`
document.head.appendChild(style)

const app = createApp(App)

app.use(createPinia())
app.use(router)
app.use(vuetify)

app.mount('#app')
